import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  clampPanelWidth,
  panelWidthBounds,
  readPanelWidth,
  rememberPanelWidth,
  type PanelWidthKey,
  type PanelWidthSpec,
} from '../panelWidth';

/** Which of the panel's own edges the handle sits on. */
export type PanelResizeEdge = 'inline-end' | 'inline-start';

const KEYBOARD_STEP = 16;
const COARSE_KEYBOARD_STEP = 64;

export interface PanelResizeOptions extends PanelWidthSpec {
  storageKey: PanelWidthKey;
  defaultWidth: number;
  edge: PanelResizeEdge;
  /** Names the panel being resized, for the separator's accessible name. */
  label: string;
  /** Width the panel shares with its neighbour; 0 until the container is measured. */
  containerWidth: number;
  /** `id` of the resized region, when the console owns that element. */
  controlsId?: string;
}

export interface PanelResizeSeparatorProps {
  role: 'separator';
  'aria-orientation': 'vertical';
  'aria-label': string;
  'aria-controls'?: string;
  'aria-valuenow': number;
  'aria-valuemin': number;
  'aria-valuemax': number;
  'data-dragging': 'true' | 'false';
  tabIndex: 0;
  onPointerDown(event: ReactPointerEvent<HTMLDivElement>): void;
  onPointerMove(event: ReactPointerEvent<HTMLDivElement>): void;
  onPointerUp(event: ReactPointerEvent<HTMLDivElement>): void;
  onPointerCancel(event: ReactPointerEvent<HTMLDivElement>): void;
  onKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void;
  onDoubleClick(): void;
}

export interface PanelResize {
  /** The width to render, already clamped to the measured container. */
  width: number;
  dragging: boolean;
  separatorProps: PanelResizeSeparatorProps;
}

/**
 * A draggable, keyboard-operable splitter for one side panel.
 *
 * The user's preference and the width actually rendered are kept apart: a
 * preference of 420px on a window that only affords 300px renders at 300px and
 * springs back to 420px when the window grows, instead of being permanently
 * truncated by having visited a narrow viewport once.
 */
export function usePanelResize({
  storageKey,
  defaultWidth,
  edge,
  label,
  containerWidth,
  controlsId,
  min,
  minComplement,
}: PanelResizeOptions): PanelResize {
  const [preferredWidth, setPreferredWidth] = useState(() => readPanelWidth(storageKey, defaultWidth));
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(null);

  const bounds = panelWidthBounds(containerWidth, { min, minComplement });
  const width = clampPanelWidth(preferredWidth, bounds);

  // Read by the pointer-up handler, which runs after the last move has already
  // re-rendered and so must not persist the width from its own stale closure.
  const widthRef = useRef(width);
  widthRef.current = width;

  const apply = useCallback((next: number, persist: boolean) => {
    const clamped = clampPanelWidth(next, bounds);
    setPreferredWidth(clamped);
    if (persist) rememberPanelWidth(storageKey, clamped);
  }, [bounds.max, bounds.min, storageKey]);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    // Secondary buttons open context menus; they must not start a drag.
    if (event.button !== 0) return;
    event.preventDefault();
    drag.current = { pointerId: event.pointerId, startX: event.clientX, startWidth: widthRef.current };
    setDragging(true);
    // Capture keeps the pointer stream on the handle once the cursor outruns
    // its 9px hit area, which it does immediately on any real drag.
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId) return;
    const delta = event.clientX - state.startX;
    apply(state.startWidth + (edge === 'inline-end' ? delta : -delta), false);
  }, [apply, edge]);

  const endDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    rememberPanelWidth(storageKey, widthRef.current);
  }, [storageKey]);

  const onKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? COARSE_KEYBOARD_STEP : KEYBOARD_STEP;
    // Arrow keys move the splitter, not the panel: pressing Left always moves
    // the divider left, whichever side of it the panel is on.
    const towardsStart = edge === 'inline-end' ? -step : step;
    let next: number;
    switch (event.key) {
      case 'ArrowLeft': next = widthRef.current + towardsStart; break;
      case 'ArrowRight': next = widthRef.current - towardsStart; break;
      case 'Home': next = edge === 'inline-end' ? bounds.min : bounds.max; break;
      case 'End': next = edge === 'inline-end' ? bounds.max : bounds.min; break;
      case 'Enter': next = defaultWidth; break;
      default: return;
    }
    event.preventDefault();
    apply(next, true);
  }, [apply, bounds.max, bounds.min, defaultWidth, edge]);

  return {
    width,
    dragging,
    separatorProps: {
      role: 'separator',
      'aria-orientation': 'vertical',
      'aria-label': label,
      ...(controlsId ? { 'aria-controls': controlsId } : {}),
      'aria-valuenow': Math.round(width),
      'aria-valuemin': Math.round(bounds.min),
      'aria-valuemax': Math.round(bounds.max),
      'data-dragging': dragging ? 'true' : 'false',
      tabIndex: 0,
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown,
      onDoubleClick: () => apply(defaultWidth, true),
    },
  };
}

/**
 * Live inline width of `element`, or 0 while there is nothing to measure.
 *
 * Measured in a layout effect first so the clamp has a real container before
 * the first paint, then kept current by a `ResizeObserver` — a window resize is
 * only one of the ways this box changes width (collapsing the tree is another).
 */
export function useElementWidth(element: HTMLElement | null): number {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!element) {
      setWidth(0);
      return;
    }
    setWidth(element.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setWidth(element.clientWidth));
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return width;
}
